/* eslint-disable max-lines -- Pre-existing violation */
import {isFeatureEnabled} from 'feature-flags';
import {openConfirmDialog, openDialog, Parameter} from 'platform/components';
import {formatPhoneNumber, useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {createSearchParams} from 'react-router-dom';

import {defaultTo, isNil, reject} from 'ramda';
import {isFalse, isNilOrEmpty, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  useGetCustomerV2Query,
  useGetUserQuery,
  useGetVehicleCustomerQuery,
} from '@dms/api/customer';
import {
  useGetBusinessCaseByBusinessCaseQuery,
  useGetVehicleOfferAlternativesQuery,
  useGetLastActiveBuyingBusinessCaseQuery,
  useGetLastActiveSellingBusinessCaseQuery,
  useGetVehicleQuery,
  useGetVehicleV2Query,
  useListHighlightsQuery,
  useListShortcomingsQuery,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetRentalVehicleQuery, useGetSaleVehicleQuery} from '@dms/api/vehicle';
import {useGetLastActiveServiceCaseQuery, useGetServiceVehicleQuery} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {
  businessCaseRoutes,
  customerRoutes,
  rentalOrderRoutes,
  testIds,
  workshopRoutes,
} from '@dms/routes';
import {
  EMPTY_PLACEHOLDER,
  formatPowerWithHorsepower,
  getBusinessCaseSnippet,
  getCustomerName,
  getEmailParameter,
  getUserName,
  queryParams,
  SnippetProps,
  StateType,
  useIsLicenceEnabled,
  usePermissions,
  useStateFlag,
  useVehicleCatalogue,
} from '@dms/shared';
import {useGetVehicleEnums} from '@dms/teas';

import {buildArray, composePath, DOT_CHARACTER, parseDate, useNavigate} from 'shared/web';

import {VehicleBuyForm} from '../(sections)/VehicleDetails/components/VehicleBuyForm';
import {VehicleSpecificationSnippetTitle} from '../components/VehicleSpecificationSnippetTitle';
import {useCreationBranchConfirmation} from './useCreationBranchConfirmation';
import {useGetManufacturedOn} from './useGetManufacturedOn';

const SALE_VEHICLE_LOCKED_SELLING_STATES = ['contract', 'payment-pending', 'sold', 'handed-over'];

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export const useGetVehicleSnippets = (vehicleId: string) => {
  const navigate = useNavigate();
  const formatNumber = useFormatNumber();
  const formatDateTime = useDateTimeFormatter();

  const {data: saleVehicle} = useGetSaleVehicleQuery({vehicleId});
  const confirmCreationBranch = useCreationBranchConfirmation({
    vehicleBranchId: saleVehicle?.branchId,
  });

  const {data: vehicleDetail} = useGetVehicleQuery({vehicleId});
  const {data: rentalVehicle} = useGetRentalVehicleQuery({vehicleId});

  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: saleVehicle?.vehicleId ?? '',
    },
    {skip: isNil(saleVehicle?.vehicleId)}
  );

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.businessCase,
      recordId: saleVehicle?.saleBusinessCaseId ?? '',
    },
    {skip: isNil(saleVehicle?.saleBusinessCaseId)}
  );
  const [
    canReadPurchaseVehicle,
    hasCreateBusinessCasePurchasePermission,
    hasCreateBusinessCaseSellingPermission,
    hasAddVehicleToBuyPermission,
    hasAddVehicleToSellPermission,
    canReadSellVehicle,
    canReadServiceCase,
    canReadAdvertisingSettings,
    canReadBusinessCase,
  ] = usePermissions({
    permissionKeys: [
      'viewBusinessCasePurchase',
      'createBusinessCasePurchase',
      'createBusinessCaseSelling',
      'addVehicleToBuy',
      'addVehicleToSell',
      'viewBusinessCaseSelling',
      'serviceCaseRead',
      'readAdvertisingSettings',
      'businessCaseRead',
    ],
    scopes: {
      readAdvertisingSettings: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      viewBusinessCasePurchase: {
        participation: businessCaseParticipation,
        // Branch check is skipped because the BE already filters results by the user's branches
        // in the last-buying/last-selling endpoints.
        branchId: 'SKIP',
      },
      viewBusinessCaseSelling: {
        participation: businessCaseParticipation,
        // Branch check is skipped because the BE already filters results by the user's branches
        // in the last-buying/last-selling endpoints.
        branchId: 'SKIP',
      },
      serviceCaseRead: {
        // We want to show SC without scopes, BE handles ACL and return correct data
        branchId: 'SKIP',
        // We want to show SC without scopes, BE handles ACL and return correct data
        serviceCaseType: 'SKIP',
      },
    },
  });

  const isOfferAlternativesEnabled = isFeatureEnabled(featureFlags.NEW_VEHICLE_CATALOGUE);
  const {data: vehicleOfferAlternatives} = useGetVehicleOfferAlternativesQuery(
    {vehicleId},
    {skip: !isOfferAlternativesEnabled || !canReadBusinessCase}
  );

  const {isWorkshopModuleEnabled} = useIsLicenceEnabled();

  const {data: serviceVehicle, isLoading} = useGetServiceVehicleQuery(
    {vehicleId},
    {skip: !isWorkshopModuleEnabled}
  );

  const hasSalesLicense = canReadBusinessCase;
  const isSaleVehicle = isNotNil(saleVehicle);
  const isServiceVehicle = isNotNil(serviceVehicle);
  const isNotServiceNorSaleVehicle = !isSaleVehicle && !isServiceVehicle;
  const isSaleVehicleDisabledForSale =
    saleVehicle?.buyingState === 'not-bought' ||
    saleVehicle?.buyingState === 'returned-to-customer' ||
    (saleVehicle?.buyingState === 'buying' && saleVehicle?.type === 'BROKERAGE') ||
    (saleVehicle?.buyingState === 'priced' && saleVehicle?.type === 'BROKERAGE');
  const isSaleVehicleBought = saleVehicle?.buyingState === 'bought';
  const isSaleVehicleSellingLocked = SALE_VEHICLE_LOCKED_SELLING_STATES.includes(
    saleVehicle?.sellingState ?? ''
  );

  const canCreatePurchase =
    hasCreateBusinessCasePurchasePermission &&
    hasAddVehicleToBuyPermission &&
    isFalse(isSaleVehicleBought);

  const canCreateSell =
    hasCreateBusinessCaseSellingPermission &&
    hasAddVehicleToSellPermission &&
    isFalse(isSaleVehicleDisabledForSale) &&
    isFalse(isSaleVehicleSellingLocked);

  const {data: vehicle} = useGetVehicleV2Query({vehicleId});
  const {data: lastActiveSC} = useGetLastActiveServiceCaseQuery(
    {vehicleId},
    {skip: !isWorkshopModuleEnabled || !canReadServiceCase}
  );
  const {data: serviceCaseCustomer} = useGetCustomerV2Query(
    {customerId: lastActiveSC?.customerId ?? ''},
    {skip: isNil(lastActiveSC?.customerId)}
  );
  const {data: serviceCaseCreator} = useGetUserQuery(
    {id: lastActiveSC?.createdBy ?? ''},
    {skip: isNil(lastActiveSC?.createdBy)}
  );
  const {data: lastActiveBuyingBC, isError: isLastActiveBuyingBCError} =
    useGetLastActiveBuyingBusinessCaseQuery({vehicleId}, {skip: !isSaleVehicle});
  const {data: lastActiveSellingBC, isError: isLastActiveSellingBCError} =
    useGetLastActiveSellingBusinessCaseQuery({vehicleId}, {skip: !isSaleVehicle});

  const {data: saleVehicleBC} = useGetBusinessCaseByBusinessCaseQuery(
    {businessCaseId: saleVehicle?.saleBusinessCaseId ?? ''},
    {skip: isNil(saleVehicle?.saleBusinessCaseId)}
  );

  const lastSellingBC = !isLastActiveSellingBCError ? lastActiveSellingBC : undefined;
  const sellingVehicleBC =
    ['contract', 'sold', 'handed-over'].includes(saleVehicle?.sellingState ?? '') &&
    isNotNil(saleVehicleBC)
      ? saleVehicleBC
      : lastSellingBC;

  const {data: vehicleHighlights} = useListHighlightsQuery({vehicleId});
  const {data: vehicleShortcoming} = useListShortcomingsQuery({vehicleId});

  const {fuel_type, transmission, drive} = useGetVehicleEnums(vehicle?.type);

  const manufacturedDate = isNotNilOrEmpty(vehicle?.manufacturedOn)
    ? parseDate(vehicle?.manufacturedOn)
    : null;

  const productionYear =
    isNotNil(manufacturedDate) && !Number.isNaN(manufacturedDate.getFullYear())
      ? manufacturedDate.getFullYear()
      : null;

  const firstRegistrationDate = isNotNil(vehicle?.firstRegistrationOn)
    ? formatDateTime('dateShort', parseDate(vehicle?.firstRegistrationOn ?? ''))
    : null;

  const mileage = isNotNil(vehicle?.mileage)
    ? `${formatNumber(vehicle?.mileage)} ${i18n.t('general.metric.km')}`
    : null;

  const numberOfPreviousOwners = isNotNil(vehicleDetail?.state?.ownerCount)
    ? `${vehicleDetail?.state?.ownerCount} ${i18n.t('entity.vehicle.labels.previousOwner')}`
    : null;

  const originCountry = isNotNil(vehicleDetail?.modelSpecification?.originCountry)
    ? `${vehicleDetail?.modelSpecification?.originCountry}`
    : null;

  const specificationsTitle = (
    <VehicleSpecificationSnippetTitle
      items={[
        {
          title: productionYear,
          tooltip: i18n.t('entity.vehicle.labels.realProductionYear'),
        },
        {
          title: mileage,
          tooltip: i18n.t('entity.vehicle.labels.mileage'),
        },
        {
          title: numberOfPreviousOwners,
          tooltip: i18n.t('entity.vehicle.labels.previousOwners'),
        },
        {
          title: originCountry,
          tooltip: i18n.t('entity.vehicle.labels.countryOfOrigin'),
        },
        {
          title: firstRegistrationDate,
          tooltip: i18n.t('entity.vehicle.labels.firstRegistration'),
        },
      ]}
    />
  );

  const shouldShowMaximalPower =
    isNotNil(vehicle?.maximalPower) && vehicle?.power !== vehicle.maximalPower;

  const specificationParameters: Parameter[] = reject(isNil, [
    fuel_type?.[vehicle?.fuelType ?? '']?.label
      ? {
          title: fuel_type?.[vehicle?.fuelType ?? '']?.label,
          tooltip: i18n.t('entity.vehicle.labels.fuel'),
          variant: 'secondary',
        }
      : undefined,
    transmission?.[vehicle?.transmission ?? '']?.label
      ? {
          title: transmission?.[vehicle?.transmission ?? '']?.label,
          tooltip: i18n.t('entity.vehicle.labels.transmission'),
          variant: 'secondary',
        }
      : undefined,
    isNotNil(vehicle?.power)
      ? {
          title: formatPowerWithHorsepower(vehicle.power),
          tooltip: i18n.t('entity.vehicle.labels.power'),
          variant: 'secondary',
        }
      : undefined,
    shouldShowMaximalPower
      ? {
          title: formatPowerWithHorsepower(Number(vehicle.maximalPower)),
          tooltip: i18n.t('entity.vehicle.labels.maximalPower'),
          variant: 'secondary',
        }
      : undefined,
    isNotNil(vehicle?.engineVolume)
      ? {
          title: `${vehicle?.engineVolume} ${i18n.t('general.metric.ccm')}`,
          tooltip: i18n.t('entity.vehicle.labels.engineCapacity'),
          variant: 'secondary',
        }
      : undefined,
    drive?.[vehicle?.drive ?? '']?.label
      ? {
          title: drive?.[vehicle?.drive ?? '']?.label,
          tooltip: i18n.t('entity.vehicle.labels.drive'),
          variant: 'secondary',
        }
      : undefined,
  ]);

  const highlightedFeaturesFlagProps = (vehicle?.features ?? [])
    .filter((feature) => feature.isHighlighted)
    .map(
      (feature) =>
        ({
          label: feature.translation,
          isSubtle: true,
          colorScheme: 'blue',
          size: 'small',
        }) as const
    );

  const premiumFeaturesFlagProps = (vehicle?.features ?? [])
    .filter((feature) => feature.isPremium)
    .map(
      (feature) =>
        ({
          label: feature.translation,
          colorScheme: 'blue',
          size: 'small',
        }) as const
    );

  const vehicleHighlightsFlagProps = (vehicleHighlights ?? []).map(
    (item) =>
      ({
        label: item.title,
        size: 'small',
      }) as const
  );

  const vehicleShortcomingFlagProps = (vehicleShortcoming ?? []).map(
    (item) =>
      ({
        label: item.title,
        isSubtle: true,
        colorScheme: 'red',
        size: 'small',
      }) as const
  );

  const onClickBuy = () => {
    openDialog(<VehicleBuyForm vehicleId={vehicleId} />, {
      id: 'vehicleBuy-dialog',
      title: i18n.t('entity.vehicle.actions.addVehicleToBuy'),
      'data-testid': 'vehicleBuy-dialog',
    });
  };
  const onClickSell = () => {
    const navigateToCreateBusinessCaseConcept = (branchId?: string) =>
      navigate({
        pathname: businessCaseRoutes.concept,
        search: createSearchParams({
          vehicleId,
          ...(isNotNil(branchId) && {[queryParams.BRANCH_ID]: branchId}),
        }).toString(),
      });

    const confirmBranchAndNavigate = () =>
      confirmCreationBranch('business-case', navigateToCreateBusinessCaseConcept);

    if (saleVehicle?.activeReservation?.paidAdvanceDeposit) {
      openConfirmDialog({
        text: i18n.t('entity.vehicle.labels.reservationWithAdvanceDepositExistsConfirmation'),
        onConfirm: confirmBranchAndNavigate,
      });
    } else {
      confirmBranchAndNavigate();
    }
  };

  const sellingBCWidget = getBusinessCaseSnippet(
    saleVehicle,
    !isLastActiveSellingBCError ? sellingVehicleBC : undefined
  );

  const buyingBCWidget = getBusinessCaseSnippet(
    saleVehicle,
    !isLastActiveBuyingBCError ? lastActiveBuyingBC : undefined,
    isNotNil(sellingBCWidget)
  );

  const highlights = [
    ...vehicleHighlightsFlagProps,
    ...vehicleShortcomingFlagProps,
    ...premiumFeaturesFlagProps,
    ...highlightedFeaturesFlagProps,
  ];

  const serviceCaseState = useStateFlag(lastActiveSC?.state as StateType);
  const serviceCaseOrderState = useStateFlag(lastActiveSC?.ordersState as StateType);

  const [vehicleUtils] = useVehicleCatalogue(vehicle?.type);

  const bodyStyle = vehicleUtils.getBodyStyle(vehicle?.bodyStyle);
  const vehicleType = vehicleUtils.getVehicleType(vehicle?.type);

  const manufacturedOn = useGetManufacturedOn(vehicle?.manufacturedOn);

  const {selection} = useGetVehicleCustomerQuery(
    {vehicleId},
    {
      skip: !vehicleId || !isServiceVehicle || !isWorkshopModuleEnabled,
      selectFromResult: (result) => ({
        ...result,
        selection: {
          vehicleOperator: result.data?.find(
            (item) => item?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR'
          ),
        },
      }),
    }
  );

  const customerQuery = useGetCustomerV2Query(
    {customerId: defaultTo('', selection.vehicleOperator?.customerId)},
    {skip: !selection.vehicleOperator}
  );

  const getVehicleOperatorParameters = () => {
    if (!customerQuery.data) {
      return [];
    }

    const email = match(customerQuery.data.discriminator)
      .with('NATURAL_PERSON', () =>
        defaultTo(EMPTY_PLACEHOLDER, customerQuery.data?.foundingPerson?.emails?.[0]?.email)
      )
      .with('BUSINESS', () =>
        defaultTo(EMPTY_PLACEHOLDER, customerQuery.data?.contacts?.[0]?.emails?.[0]?.email)
      )
      .otherwise(() => EMPTY_PLACEHOLDER);

    const phoneNumber = match(customerQuery.data.discriminator)
      .with('NATURAL_PERSON', () => {
        const phoneNumberRaw = customerQuery.data?.foundingPerson?.phoneNumbers?.[0];

        return phoneNumberRaw
          ? formatPhoneNumber(`${phoneNumberRaw.prefix}${phoneNumberRaw.number}`)
          : EMPTY_PLACEHOLDER;
      })
      .with('BUSINESS', () => {
        const phoneNumberRaw = customerQuery.data?.contacts?.[0]?.phoneNumbers?.[0];

        return phoneNumberRaw
          ? formatPhoneNumber(`${phoneNumberRaw.prefix}${phoneNumberRaw.number}`)
          : EMPTY_PLACEHOLDER;
      })
      .otherwise(() => EMPTY_PLACEHOLDER);

    return [phoneNumber, getEmailParameter(email)];
  };

  // eslint-disable-next-line complexity -- Pre-existing violation
  const getSnippets = (): SnippetProps[] => {
    if (isServiceVehicle && isWorkshopModuleEnabled) {
      return buildArray<SnippetProps>()
        .add({
          label: i18n.t('entity.vehicle.labels.specification'),
          title: [vehicleType, bodyStyle, manufacturedOn]
            .filter(Boolean)
            .join(` ${DOT_CHARACTER} `),
          parameters: isNotNilOrEmpty(specificationParameters)
            ? specificationParameters
            : [EMPTY_PLACEHOLDER],
          'data-testid': testIds.vehicles.detail('headerSnippet-specification'),
        })
        .when(hasSalesLicense, {
          label: i18n.t('entity.vehicle.labels.highlights'),
          flags: highlights,
          placeholder: {title: i18n.t('entity.vehicle.labels.noHighlights')},
          isDisabled: isNilOrEmpty(highlights),
          'data-testid': testIds.vehicles.detail('headerSnippet-highlights'),
        })
        .add({
          label: i18n.t('entity.ownership.label.vehicleOperator'),
          title: getCustomerName(customerQuery.data),
          href: customerQuery.data
            ? composePath(customerRoutes.detail, {
                params: {id: customerQuery.data.id},
                isSearchParamsPreserved: false,
              })
            : undefined,
          target: '_blank',
          parameters: reject(isNil, getVehicleOperatorParameters()),
          isDisabled: isNilOrEmpty(customerQuery.data),
          'data-testid': testIds.vehicles.detail('headerSnippet-operator'),
        })
        .when(canReadServiceCase, {
          label: i18n.t('page.vehicle.labels.lastActiveServiceCase'),
          placeholder: {
            title: i18n.t('page.workshop.labels.newServiceCase'),
            onClick: () =>
              navigate(
                composePath(workshopRoutes.createServiceCase, {
                  queryParams: {vehicleId},
                  isSearchParamsPreserved: false,
                })
              ),
          },
          href: lastActiveSC
            ? composePath(workshopRoutes.serviceCaseDetail, {
                params: {id: lastActiveSC?.id},
                isSearchParamsPreserved: false,
              })
            : undefined,
          target: '_blank',
          title: lastActiveSC?.number || '',
          flags: reject(isNil, [serviceCaseState, serviceCaseOrderState]),
          parameters: reject(isNil, [
            lastActiveSC?.createdAt
              ? formatDateTime('dateShort', parseDate(lastActiveSC?.createdAt))
              : undefined,
            `${i18n.t('general.labels.createdBy')}: ${getUserName(serviceCaseCreator)}`,
          ]),
          'data-testid': testIds.vehicles.detail('headerSnippet-lastActiveService'),
        });
    }

    // display logic
    // https://carvago.atlassian.net/browse/T20-34094
    // https://carvago.atlassian.net/browse/T20-67723
    return buildArray<SnippetProps>([
      {
        label: i18n.t('entity.vehicle.labels.specification'),
        parameters: isNotNilOrEmpty(specificationParameters)
          ? specificationParameters
          : [EMPTY_PLACEHOLDER],
        title: specificationsTitle,
        'data-testid': testIds.vehicles.detail('headerSnippet-specification'),
      },
    ])
      .when(canReadAdvertisingSettings && !isNotServiceNorSaleVehicle && hasSalesLicense, {
        label: i18n.t('entity.vehicle.labels.highlights'),
        flags: highlights,
        placeholder: {title: i18n.t('entity.vehicle.labels.noHighlights')},
        isDisabled: isNilOrEmpty(highlights),
        'data-testid': testIds.vehicles.detail('headerSnippet-highlights'),
      })
      .when(isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3) && rentalVehicle?.active, {
        label: i18n.t('entity.vehicle.labels.lastRentalOrder'),
        placeholder: {
          title: i18n.t('entity.vehicle.labels.lastRentalOrder.empty'),
        },
        title: rentalVehicle?.lastRentalOrder?.number,
        parameters:
          isNotNilOrEmpty(rentalVehicle) && isNotNilOrEmpty(rentalVehicle?.lastRentalOrder)
            ? reject(isNil, [
                formatDateTime(
                  'dateShort',
                  parseDate(rentalVehicle?.lastRentalOrder?.createdAt ?? '')
                ),
                `${formatDateTime(
                  'dateTimeShort',
                  parseDate(rentalVehicle?.lastRentalOrder?.rentalPeriodFrom ?? '')
                )} - ${formatDateTime(
                  'dateTimeShort',
                  parseDate(rentalVehicle?.lastRentalOrder?.rentalPeriodTo ?? '')
                )}`,
              ])
            : undefined,
        href: rentalVehicle?.lastRentalOrder?.rentalOrderId
          ? composePath(rentalOrderRoutes.vehicleAndCustomer, {
              params: {id: rentalVehicle?.lastRentalOrder?.rentalOrderId},
              isSearchParamsPreserved: false,
            })
          : undefined,
        target: '_blank',
      })
      .when(isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3) && rentalVehicle?.active, {
        label: i18n.t('entity.vehicle.labels.nextRentalOrder'),
        placeholder: {
          title: i18n.t('entity.vehicle.labels.nextRentalOrder.empty'),
        },
        title: rentalVehicle?.upcomingRentalOrder?.number,
        parameters:
          isNotNilOrEmpty(rentalVehicle) && isNotNilOrEmpty(rentalVehicle?.upcomingRentalOrder)
            ? reject(isNil, [
                formatDateTime(
                  'dateShort',
                  parseDate(rentalVehicle?.upcomingRentalOrder?.createdAt ?? '')
                ),
                `${formatDateTime(
                  'dateTimeShort',
                  parseDate(rentalVehicle?.upcomingRentalOrder?.rentalPeriodFrom ?? '')
                )} - ${formatDateTime(
                  'dateTimeShort',
                  parseDate(rentalVehicle?.upcomingRentalOrder?.rentalPeriodTo ?? '')
                )}`,
              ])
            : undefined,
        href: rentalVehicle?.upcomingRentalOrder?.rentalOrderId
          ? composePath(rentalOrderRoutes.vehicleAndCustomer, {
              params: {id: rentalVehicle?.upcomingRentalOrder?.rentalOrderId},
              isSearchParamsPreserved: false,
            })
          : undefined,
        target: '_blank',
      })
      .when(isServiceVehicle, {
        label: i18n.t('entity.ownership.label.vehicleOperator'),
        title: getCustomerName(customerQuery.data),
        href: customerQuery.data
          ? composePath(customerRoutes.detail, {
              params: {id: customerQuery.data.id},
              isSearchParamsPreserved: false,
            })
          : undefined,
        target: '_blank',
        parameters: reject(isNil, getVehicleOperatorParameters()),
        isDisabled: isNilOrEmpty(customerQuery.data),
        'data-testid': testIds.vehicles.detail('headerSnippet-operator'),
      })
      .when(
        isWorkshopModuleEnabled &&
          canReadServiceCase &&
          (isServiceVehicle || isNotServiceNorSaleVehicle),
        {
          label: i18n.t('page.vehicle.labels.lastActiveServiceCase'),
          placeholder: {
            title: i18n.t('page.workshop.labels.newServiceCase'),
            onClick: () =>
              navigate(
                composePath(workshopRoutes.createServiceCase, {
                  queryParams: {vehicleId},
                  isSearchParamsPreserved: false,
                })
              ),
          },
          href: lastActiveSC
            ? composePath(workshopRoutes.serviceCaseDetail, {
                params: {id: lastActiveSC?.id},
                isSearchParamsPreserved: false,
              })
            : undefined,
          title: getCustomerName(serviceCaseCustomer),
          icon: 'action/build',
          flags: reject(isNil, [serviceCaseState, serviceCaseOrderState]),
          onClick: () =>
            navigate(
              composePath(workshopRoutes.serviceCaseDetail, {
                params: {id: lastActiveSC?.id},
                isSearchParamsPreserved: false,
              })
            ),
          parameters: reject(isNil, [
            lastActiveSC?.number,
            `${i18n.t('general.labels.createdBy')}: ${getUserName(serviceCaseCreator)}`,
          ]),
        }
      )
      .when(
        (isSaleVehicle || isNotServiceNorSaleVehicle) && canReadPurchaseVehicle && hasSalesLicense,
        {
          label: i18n.t('page.businessCase.labels.activeSellingBusinessCase'),
          placeholder: {
            title: i18n.t('entity.vehicle.labels.buyVehicle'),
            onClick: onClickBuy,
            isDisabled: !canCreatePurchase,
          },
          'data-testid': testIds.vehicles.detail('headerSnippet-sellingBc'),
          ...buyingBCWidget,
        }
      )
      .when(
        (isSaleVehicle || isNotServiceNorSaleVehicle) && canReadSellVehicle && hasSalesLicense,
        {
          label: i18n.t('page.businessCase.labels.activeBuyingBusinessCase'),
          placeholder: {
            title: i18n.t('entity.vehicle.labels.sellVehicle'),
            onClick: onClickSell,
            isDisabled: !canCreateSell,
          },
          'data-testid': testIds.vehicles.detail('headerSnippet-buyingBc'),
          ...sellingBCWidget,
        }
      );
  };

  // display logic
  // https://carvago.atlassian.net/browse/T20-34094
  // https://carvago.atlassian.net/browse/T20-67723
  // https://carvago.atlassian.net/browse/T20-84143
  // eslint-disable-next-line complexity -- Pre-existing violation
  const getSnippetsV3 = (): SnippetProps[] =>
    buildArray<SnippetProps>([
      {
        label: i18n.t('entity.vehicle.labels.specification'),
        parameters: isNotNilOrEmpty(specificationParameters)
          ? specificationParameters
          : [EMPTY_PLACEHOLDER],
        title: specificationsTitle,
        'data-testid': testIds.vehicles.detail('headerSnippet-specification'),
      },
    ])
      .when(canReadAdvertisingSettings && isSaleVehicle && hasSalesLicense, {
        label: i18n.t('entity.vehicle.labels.highlights'),
        flags: highlights,
        placeholder: {title: i18n.t('entity.vehicle.labels.noHighlights')},
        isDisabled: isNilOrEmpty(highlights),
        'data-testid': testIds.vehicles.detail('headerSnippet-highlights'),
      })
      .when(rentalVehicle?.active, {
        label: i18n.t('entity.vehicle.labels.lastRentalOrder'),
        placeholder: {
          title: i18n.t('entity.vehicle.labels.lastRentalOrder.empty'),
        },
        title: rentalVehicle?.lastRentalOrder?.number,
        parameters:
          isNotNilOrEmpty(rentalVehicle) && isNotNilOrEmpty(rentalVehicle?.lastRentalOrder)
            ? reject(isNil, [
                formatDateTime(
                  'dateShort',
                  parseDate(rentalVehicle?.lastRentalOrder?.createdAt ?? '')
                ),
                `${formatDateTime(
                  'dateTimeShort',
                  parseDate(rentalVehicle?.lastRentalOrder?.rentalPeriodFrom ?? '')
                )} - ${formatDateTime(
                  'dateTimeShort',
                  parseDate(rentalVehicle?.lastRentalOrder?.rentalPeriodTo ?? '')
                )}`,
              ])
            : undefined,
        href: rentalVehicle?.lastRentalOrder?.rentalOrderId
          ? composePath(rentalOrderRoutes.vehicleAndCustomer, {
              params: {id: rentalVehicle?.lastRentalOrder?.rentalOrderId},
              isSearchParamsPreserved: false,
            })
          : undefined,
        target: '_blank',
      })
      .when(rentalVehicle?.active, {
        label: i18n.t('entity.vehicle.labels.nextRentalOrder'),
        placeholder: {
          title: i18n.t('entity.vehicle.labels.nextRentalOrder.empty'),
        },
        title: rentalVehicle?.upcomingRentalOrder?.number,
        parameters:
          isNotNilOrEmpty(rentalVehicle) && isNotNilOrEmpty(rentalVehicle?.upcomingRentalOrder)
            ? reject(isNil, [
                formatDateTime(
                  'dateShort',
                  parseDate(rentalVehicle?.upcomingRentalOrder?.createdAt ?? '')
                ),
                `${formatDateTime(
                  'dateTimeShort',
                  parseDate(rentalVehicle?.upcomingRentalOrder?.rentalPeriodFrom ?? '')
                )} - ${formatDateTime(
                  'dateTimeShort',
                  parseDate(rentalVehicle?.upcomingRentalOrder?.rentalPeriodTo ?? '')
                )}`,
              ])
            : undefined,
        href: rentalVehicle?.upcomingRentalOrder?.rentalOrderId
          ? composePath(rentalOrderRoutes.vehicleAndCustomer, {
              params: {id: rentalVehicle?.upcomingRentalOrder?.rentalOrderId},
              isSearchParamsPreserved: false,
            })
          : undefined,
        target: '_blank',
      })
      .when(isServiceVehicle, {
        label: i18n.t('entity.ownership.label.vehicleOperator'),
        title: getCustomerName(customerQuery.data),
        href: customerQuery.data
          ? composePath(customerRoutes.detail, {
              params: {id: customerQuery.data.id},
              isSearchParamsPreserved: false,
            })
          : undefined,
        target: '_blank',
        parameters: reject(isNil, getVehicleOperatorParameters()),
        isDisabled: isNilOrEmpty(customerQuery.data),
        'data-testid': testIds.vehicles.detail('headerSnippet-operator'),
      })
      .when(
        (isServiceVehicle || isNotServiceNorSaleVehicle) &&
          isWorkshopModuleEnabled &&
          canReadServiceCase,
        {
          label: i18n.t('page.vehicle.labels.lastActiveServiceCase'),
          placeholder: {
            title: i18n.t('page.workshop.labels.newServiceCase'),
            onClick: () =>
              navigate(
                composePath(workshopRoutes.createServiceCase, {
                  queryParams: {vehicleId},
                  isSearchParamsPreserved: false,
                })
              ),
          },
          href: lastActiveSC
            ? composePath(workshopRoutes.serviceCaseDetail, {
                params: {id: lastActiveSC?.id},
                isSearchParamsPreserved: false,
              })
            : undefined,
          target: '_blank',
          title: lastActiveSC?.number || '',
          flags: reject(isNil, [serviceCaseState, serviceCaseOrderState]),
          parameters: reject(isNil, [
            lastActiveSC?.createdAt
              ? formatDateTime('dateShort', parseDate(lastActiveSC?.createdAt))
              : undefined,
            `${i18n.t('general.labels.createdBy')}: ${getUserName(serviceCaseCreator)}`,
          ]),
          'data-testid': testIds.vehicles.detail('headerSnippet-lastActiveService'),
        }
      )
      .when(
        isSaleVehicle &&
          canReadPurchaseVehicle &&
          hasSalesLicense &&
          saleVehicle?.sellingState !== 'not-for-sale',
        {
          label: i18n.t('page.businessCase.labels.activeSellingBusinessCase'),
          placeholder: {
            title: i18n.t('entity.vehicle.labels.buyVehicle'),
            onClick: onClickBuy,
            isDisabled: !canCreatePurchase,
          },
          'data-testid': testIds.vehicles.detail('headerSnippet-sellingBc'),
          ...buyingBCWidget,
        }
      )
      .when(
        isSaleVehicle &&
          canReadSellVehicle &&
          hasSalesLicense &&
          saleVehicle?.sellingState !== 'not-for-sale',
        {
          label: i18n.t('page.businessCase.labels.activeBuyingBusinessCase'),
          placeholder: {
            title: i18n.t('entity.vehicle.labels.sellVehicle'),
            onClick: onClickSell,
            isDisabled: !canCreateSell,
          },
          'data-testid': testIds.vehicles.detail('headerSnippet-buyingBc'),
          ...sellingBCWidget,
        }
      );

  // A stock vehicle offered as a not-yet-applied alternative: the deal-vehicle snippets above do
  // not know about it, so link the Business Case that offers it.
  const offeredInBusinessCaseSnippets: SnippetProps[] = isOfferAlternativesEnabled
    ? (vehicleOfferAlternatives?.items ?? [])
        .filter((item) => !item.isApplied)
        .map((item) => ({
          label: i18n.t('entity.offerAlternative.vehicleSnippet.label', {
            defaultValue: 'Offered in business case',
          }),
          // "BC000257 · Alternative C"; the letter alone while the code is unknown.
          title: [
            item.businessCasePublicId,
            i18n.t('entity.offerAlternative.label', {
              defaultValue: 'Alternative {letter}',
              letter: item.letter,
            }),
          ]
            .filter(isNotNilOrEmpty)
            .join(' · '),
          icon: 'places/business_center',
          href: composePath(businessCaseRoutes.selling, {
            params: {id: item.businessCaseId},
            isSearchParamsPreserved: false,
          }),
          'data-testid': testIds.vehicles.detail(`headerSnippet-offeredIn-${item.businessCaseId}`),
        }))
    : [];

  const snippets = [
    ...(isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3) ? getSnippetsV3() : getSnippets()),
    ...offeredInBusinessCaseSnippets,
  ];

  return {snippets, isLoading};
};
